import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { ExpressionGroup } from './components/ExpressionGroup';
import { useAdvancedExpressionBuilder } from './hooks/useAdvancedExpressionBuilder';
import { AdvancedExpressionBuilderProps, defaultLocale } from './types';
import { buildConditionDefinition } from './utils/buildConditionDefinition';

export const AdvancedExpressionBuilder: React.FC<AdvancedExpressionBuilderProps> = ({
  initialExpressions,
  onChange,
  fetchFieldCatalog,
  fetchResourceValues,
  isLoading = false,
  locale = {},
}) => {
  // Merge provided locale with defaults
  const mergedLocale = { ...defaultLocale, ...locale };

  // Use the custom hook for state management
  const {
    state,
    createGroup,
    removeGroup,
    switchGroupsOperator,
    createExpression,
    removeExpression,
    updateExpression,
    switchExpressionsOperator,
    resetState,
  } = useAdvancedExpressionBuilder({
    initialExpressions,
    onChange,
  });

  const groupEntries = Object.entries(state.groups || {});
  const groupCount = groupEntries.length;

  const [viewMode, setViewMode] = useState<'builder' | 'json'>('builder');

  const handleGroupsOperatorChange = (
    _: React.MouseEvent<HTMLElement>,
    newOperator: 'AND' | 'OR' | null,
  ) => {
    if (newOperator) {
      switchGroupsOperator(newOperator);
    }
  };

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'builder' | 'json' | null,
  ) => {
    if (newMode) {
      setViewMode(newMode);
    }
  };

  const handleAddGroup = () => {
    createGroup();
  };

  const handleReset = () => {
    resetState();
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      minHeight: 200, 
      width: '100%'
    }}>
      {/* Loading Backdrop */}
      {isLoading && (
        <Backdrop
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
          open={isLoading}
        >
          <CircularProgress />
        </Backdrop>
      )}

      {/* Main Container */}
      <Box 
        id="advanced-expression-builder"
        sx={{ 
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          mb: { xs: 1.5, md: 2 },
          gap: { xs: 1.5, sm: 0 },
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 1, sm: 1.5 } 
          }}>
            
            {/* Global Operator Toggle - Only show if there are multiple groups */}
            {groupCount > 1 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Match:
                </Typography>
                <ToggleButtonGroup
                  value={state.operator}
                  exclusive
                  onChange={handleGroupsOperatorChange}
                  size="small"
                  sx={{ height: 32 }}
                >
                  <ToggleButton value="AND" sx={{ px: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {mergedLocale.andOperator}
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="OR" sx={{ px: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {mergedLocale.orOperator}
                    </Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary">
                  of these groups
                </Typography>
              </>
            )}
          </Box>

          {/* View Toggle and Reset Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              aria-label="View mode"
            >
              <ToggleButton value="builder" aria-label="Builder view">
                Builder
              </ToggleButton>
              <ToggleButton value="json" aria-label="JSON view">
                JSON
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleReset}
              sx={{ textTransform: 'none' }}
            >
              Reset
            </Button>
          </Box>
        </Box>

        {/* Conditional View */}
        {viewMode === 'builder' ? (
          <>
            {/* Expression Groups */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
              {groupEntries.map(([groupId, group], index) => (
                <Box key={groupId} sx={{ width: '100%' }}>
                  <ExpressionGroup
                    group={group}
                    groupId={groupId}
                    onUpdateExpression={updateExpression}
                    onRemoveExpression={removeExpression}
                    onCreateExpression={createExpression}
                    onRemoveGroup={removeGroup}
                    onSwitchExpressionsOperator={switchExpressionsOperator}
                    fetchFieldCatalog={fetchFieldCatalog}
                    fetchResourceValues={fetchResourceValues}
                    locale={mergedLocale}
                    showRemoveButton={groupCount > 1}
                    showGroupOperatorToggle={true}
                  />

                  {/* OR Indicator between groups */}
                  {index < groupCount - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: state.operator === 'AND' ? '#2196f3' : '#ff9800',
                          color: 'white',
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                        }}
                      >
                        {state.operator}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Add Group Button */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddGroup}
                size="medium"
                sx={{
                  textTransform: 'none',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  color: '#ff9800',
                  borderColor: '#ff9800',
                  '&:hover': {
                    borderStyle: 'solid',
                    backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  },
                }}
              >
                {mergedLocale.addOrGroupButton}
              </Button>
            </Box>
          </>
        ) : (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(buildConditionDefinition(state), null, 2)}
          </Paper>
        )}

        {/* Summary */}
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Summary:</strong> {groupCount} group{groupCount !== 1 ? 's' : ''} with{' '}
            {Object.values(state.groups || {}).reduce((total, group) => 
              total + Object.keys(group.expressions).length, 0
            )} total expression{Object.values(state.groups || {}).reduce((total, group) => 
              total + Object.keys(group.expressions).length, 0
            ) !== 1 ? 's' : ''}
          </Typography>
          
          {/* Completion Status */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Status:</strong>{' '}
            {Object.values(state.groups || {}).every(group => 
              Object.values(group.expressions).every(expr => expr.isCompleted)
            ) && Object.values(state.groups || {}).length > 0 ? (
              <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Complete</span>
            ) : (
              <span style={{ color: '#f44336', fontWeight: 'bold' }}>Incomplete</span>
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvancedExpressionBuilder;